import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './button';
import { Textarea } from './textarea';
import { RadioGroup, RadioGroupItem } from './radio-group';
import { Label } from './label';
import { ThumbsUp, ThumbsDown, AlertCircle, MessageSquare, Bot } from 'lucide-react';
import { toast } from 'sonner';

interface VoteWidgetProps {
  contentType: 'diagram' | 'course' | 'post';
  contentId: string;
  onVoteSubmit?: (voteData: VoteData) => void;
}

interface VoteData {
  contentType: string;
  contentId: string;
  voteType: 'accurate' | 'inaccurate' | 'needs_improvement' | 'custom';
  customFeedback?: string;
}

const VoteWidget = ({ contentType, contentId, onVoteSubmit }: VoteWidgetProps) => {
  // Tạm thời ẩn cơ chế vote
  return null;
  
  // Phần code bên dưới sẽ được giữ lại nhưng không được sử dụng
  // Khi nào cần kích hoạt lại, chỉ cần xóa dòng "return null;" ở trên
  
  const { t } = useTranslation('common');
  const [voteType, setVoteType] = useState<VoteData['voteType'] | null>(null);
  const [customFeedback, setCustomFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasVoted, setHasVoted] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);

  // Kiểm tra xem người dùng đã vote chưa
  useEffect(() => {
    const cookieName = `voted_${contentType}_${contentId}`;
    const hasUserVoted = document.cookie.includes(cookieName);
    setHasVoted(hasUserVoted);
  }, [contentType, contentId]);

  const handleVoteSubmit = async () => {
    if (voteType === 'custom' && !customFeedback.trim()) {
      toast.error(t('vote.enterFeedback'));
      return;
    }

    setIsSubmitting(true);

    try {
      const voteData: VoteData = {
        contentType,
        contentId,
        voteType: voteType || 'accurate',
        ...(customFeedback.trim() && { customFeedback }),
      };

      // Danh sách các endpoint có thể
      const possibleEndpoints = [
        'http://localhost:3001/votes',
        'http://localhost:3001/api/votes',
        '/votes',
        '/api/votes'
      ];
      
      let response = null;
      let error = null;
      
      // Thử từng endpoint cho đến khi thành công
      for (const endpoint of possibleEndpoints) {
        try {
          response = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Origin': window.location.origin,
            },
            credentials: 'include', // Gửi cookies nếu cần
            body: JSON.stringify(voteData),
          });
          
          if (response.ok) {
            console.log(`Thành công với endpoint: ${endpoint}`);
            break; // Thoát vòng lặp nếu thành công
          }
        } catch (err) {
          error = err;
          console.log(`Lỗi với endpoint ${endpoint}:`, err);
          // Tiếp tục thử endpoint tiếp theo
        }
      }

      // Nếu không có response hoặc tất cả đều thất bại
      if (!response || !response.ok) {
        throw error || new Error('Không thể kết nối đến server. Vui lòng thử lại sau.');
      }

      // Lưu cookie để đánh dấu đã vote
      const cookieName = `voted_${contentType}_${contentId}`;
      const cookieValue = 'true';
      const cookieExpiry = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000); // 30 days
      document.cookie = `${cookieName}=${cookieValue};expires=${cookieExpiry.toUTCString()};path=/`;

      setHasVoted(true);
      toast.success(t('vote.thankYou'));

      // Callback nếu có
      if (onVoteSubmit) {
        onVoteSubmit(voteData);
      }
    } catch (error) {
      console.error('Error submitting vote:', error);
      toast.error(t('vote.error', 'Không thể gửi đánh giá. Vui lòng thử lại sau.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleShowFeedbackForm = () => {
    setShowFeedbackForm(true);
  };

  if (hasVoted) {
    return (
      <div className="bg-muted/30 rounded-lg p-4 border border-border">
        <div className="flex items-center gap-2 text-muted-foreground">
          <ThumbsUp className="h-4 w-4" />
          <p>{t('vote.alreadyVoted')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-muted/30 rounded-lg p-4 border border-border">
      <div className="flex items-center gap-2 mb-4">
        <Bot className="h-5 w-5 text-primary" />
        <p className="text-sm text-muted-foreground">{t('vote.aiGeneratedContent')}</p>
      </div>

      {!showFeedbackForm ? (
        <div className="flex flex-col space-y-4">
          <p className="text-sm font-medium">{t('vote.helpUsImprove')}</p>
          <div className="flex flex-wrap gap-2">
            <Button 
              variant="outline" 
              size="sm"
              className="flex items-center gap-2"
              onClick={() => {
                setVoteType('accurate');
                setTimeout(() => handleVoteSubmit(), 0);
              }}
            >
              <ThumbsUp className="h-4 w-4" />
              {t('vote.accurate')}
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              className="flex items-center gap-2"
              onClick={() => {
                setVoteType('inaccurate');
                setShowFeedbackForm(true);
              }}
            >
              <ThumbsDown className="h-4 w-4" />
              {t('vote.needsAdjustment', 'Cần điều chỉnh')}
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              className="flex items-center gap-2"
              onClick={handleShowFeedbackForm}
            >
              <MessageSquare className="h-4 w-4" />
              {t('vote.leaveFeedback')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm font-medium">{t('vote.yourFeedback')}</p>
          
          <RadioGroup value={voteType || ''} onValueChange={(value) => setVoteType(value as VoteData['voteType'])}>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="accurate" id="accurate" />
              <Label htmlFor="accurate" className="flex items-center gap-2">
                <ThumbsUp className="h-4 w-4" />
                {t('vote.accurate')}
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="inaccurate" id="inaccurate" />
              <Label htmlFor="inaccurate" className="flex items-center gap-2">
                <ThumbsDown className="h-4 w-4" />
                {t('vote.needsAdjustment', 'Cần điều chỉnh')}
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="needs_improvement" id="needs_improvement" />
              <Label htmlFor="needs_improvement" className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                {t('vote.needsImprovement')}
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="custom" id="custom" />
              <Label htmlFor="custom">{t('vote.customFeedback')}</Label>
            </div>
          </RadioGroup>

          {voteType && voteType !== 'accurate' && (
            <Textarea
              placeholder={
                voteType === 'inaccurate' 
                  ? t('vote.whyNeedsAdjustment', 'Vui lòng cho biết lý do cần điều chỉnh...') 
                  : voteType === 'needs_improvement' 
                    ? t('vote.howToImprove', 'Vui lòng cho biết cách cải thiện...') 
                    : t('vote.enterYourFeedback')
              }
              value={customFeedback}
              onChange={(e) => setCustomFeedback(e.target.value)}
              className="min-h-[100px]"
            />
          )}

          <div className="flex justify-end gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowFeedbackForm(false)}
              disabled={isSubmitting}
            >
              {t('cancel')}
            </Button>
            <Button 
              variant="default" 
              size="sm"
              onClick={handleVoteSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? t('submitting', 'Đang gửi...') : t('submit')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoteWidget; 